/**
 * 5897 — Gráficos SVG das telas de Previsões (linhas, barras horizontais,
 * barras empilhadas, pontos de calibração e faixa em escala log).
 * Sem biblioteca, como os do Borderô: todos com legenda, rótulo direto e
 * dica ao passar o mouse; cores da paleta de séries do Borderô.
 */
import { FC, ReactNode, useRef, useState } from 'react'
import styled from 'styled-components'
import { BD, Card } from '../styles'
import { Entenda } from './comum'

/** Séries em ordem fixa (paleta do Borderô) */
export const SERIE = ['#2A3B4C', '#FF4C3E', '#4CAF50', '#FFC107']
const EIXO = '#6B7280'
const GRADE = '#E5E7EB'
const TINTA = '#374151'

export const pctFmt = (v: number): string => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`

const Caixa = styled(Card)`
  position: relative;
  min-width: 0;
  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
  }
  .sub {
    font-size: 12.5px;
    color: ${BD.grayText};
    margin: 2px 0 6px;
  }
  svg {
    display: block;
    width: 100%;
    height: auto;
    overflow: visible;
  }
`
const Legenda = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  font-size: 12.5px;
  color: ${TINTA};
  margin: 4px 0;
  i {
    display: inline-block;
    width: 14px;
    height: 3px;
    border-radius: 2px;
    vertical-align: middle;
    margin-right: 6px;
  }
  i.caixa {
    width: 10px;
    height: 10px;
  }
`
const Dica = styled.div`
  position: absolute;
  pointer-events: none;
  background: #fff;
  border: 1px solid ${BD.grayBorder};
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  padding: 8px 10px;
  font-size: 12.5px;
  z-index: 5;
  min-width: 150px;
  .h {
    color: ${BD.grayText};
    font-size: 11.5px;
    margin-bottom: 2px;
  }
  .r {
    display: flex;
    justify-content: space-between;
    gap: 12px;
  }
  .r i {
    display: inline-block;
    width: 10px;
    height: 3px;
    border-radius: 2px;
    margin-right: 6px;
    vertical-align: middle;
  }
  b {
    font-variant-numeric: tabular-nums;
  }
`

export interface Serie {
  nome: string
  valores: Array<number | null>
  /** x próprio (gráfico de pontos) */
  x?: number[]
  tracejada?: boolean
}

interface EstadoDica {
  left: number
  top: number
  titulo: string
  linhas: Array<{ nome: string; cor: string; valor: string }>
}

const VerTabela = styled.details`
  margin-top: 8px;
  font-size: 12.5px;
  summary {
    cursor: pointer;
    color: ${BD.grayText};
  }
  .rolagem {
    overflow-x: auto;
  }
  table {
    border-collapse: collapse;
    margin-top: 6px;
    font-size: 12.5px;
  }
  th,
  td {
    padding: 4px 10px;
    border-bottom: 1px solid ${BD.grayBorder};
    text-align: right;
    white-space: nowrap;
  }
  th:first-child,
  td:first-child {
    text-align: left;
  }
`

/** Os mesmos dados do gráfico em tabela, para teclado e leitor de tela. */
export interface TabelaDados {
  cabecalho: string[]
  linhas: string[][]
}

/** Moldura comum: título, subtítulo, legenda, svg, dica, tabela dos dados e "Entenda melhor". */
const Moldura: FC<{
  titulo: string
  sub?: string
  legenda?: Array<{ nome: string; cor: string; caixa?: boolean }>
  entenda?: string[]
  dica: EstadoDica | null
  caixaRef: React.RefObject<HTMLDivElement | null>
  dados: TabelaDados
  children: ReactNode
}> = ({ titulo, sub, legenda, entenda, dica, caixaRef, dados, children }) => (
  <Caixa ref={caixaRef}>
    <h3>{titulo}</h3>
    {sub && <p className="sub">{sub}</p>}
    {legenda && legenda.length > 1 && (
      <Legenda>
        {legenda.map((l) => (
          <span key={l.nome}>
            <i className={l.caixa ? 'caixa' : undefined} style={{ background: l.cor }} />
            {l.nome}
          </span>
        ))}
      </Legenda>
    )}
    {children}
    {dica && (
      <Dica style={{ left: dica.left, top: dica.top }}>
        <div className="h">{dica.titulo}</div>
        {dica.linhas.map((l) => (
          <div className="r" key={l.nome}>
            <span>
              <i style={{ background: l.cor }} />
              {l.nome}
            </span>
            <b>{l.valor}</b>
          </div>
        ))}
      </Dica>
    )}
    <VerTabela>
      <summary>Ver em tabela</summary>
      <div className="rolagem">
        <table>
          <caption style={{ position: 'absolute', left: -9999 }}>{titulo}</caption>
          <thead>
            <tr>
              {dados.cabecalho.map((c) => (
                <th key={c} scope="col">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dados.linhas.map((l, i) => (
              <tr key={i}>
                {l.map((v, j) => (
                  <td key={j}>{v}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </VerTabela>
    {entenda && <Entenda paragrafos={entenda} />}
  </Caixa>
)

/** posição da dica dentro da caixa, sem sair pela direita */
function posicao(caixa: HTMLDivElement | null, ev: { clientX: number; clientY: number }): { left: number; top: number } {
  if (!caixa) return { left: 0, top: 0 }
  const bb = caixa.getBoundingClientRect()
  return { left: Math.max(8, Math.min(ev.clientX - bb.left + 14, caixa.clientWidth - 200)), top: Math.max(8, ev.clientY - bb.top - 10) }
}

/* ── linhas (curvas, erro por antecedência, calibração com pontos) ── */
export const GraficoLinhas: FC<{
  titulo: string
  sub?: string
  entenda?: string[]
  xs: number[]
  series: Serie[]
  /** eixo x decrescente (D-60 → D0) */
  invertido?: boolean
  ymax: number
  yticks: number[]
  xticks: number[]
  fmtX: (x: number) => string
  fmtY?: (y: number) => string
  tituloDica: (i: number) => string
  /** só pontos, com diagonal de referência (calibração) */
  pontos?: boolean
  altura?: number
}> = ({ titulo, sub, entenda, xs, series, invertido, ymax, yticks, xticks, fmtX, fmtY = pctFmt, tituloDica, pontos, altura = 280 }) => {
  const caixaRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const [dica, setDica] = useState<EstadoDica | null>(null)
  const [cruz, setCruz] = useState<number | null>(null)
  const W = 560
  const H = altura
  const M = { l: 48, r: pontos ? 16 : 120, t: 12, b: 30 }
  const todosX = pontos ? series.flatMap((s) => s.x ?? []) : xs
  const xmin = pontos ? 0 : Math.min(...todosX)
  const xmax = pontos ? 1 : Math.max(...todosX)
  const x = (v: number): number =>
    invertido ? M.l + ((W - M.l - M.r) * (xmax - v)) / (xmax - xmin) : M.l + ((W - M.l - M.r) * (v - xmin)) / (xmax - xmin)
  const y = (v: number): number => H - M.b - ((H - M.t - M.b) * v) / ymax
  const cor = (i: number): string => SERIE[i % SERIE.length]

  // rótulos no fim das linhas, afastados para não colidir
  const rotulos = pontos
    ? []
    : series
        .map((s, i) => {
          let ult = -1
          s.valores.forEach((v, j) => {
            if (v !== null) ult = j
          })
          return { nome: s.nome, cor: cor(i), x: x(xs[ult]) + 8, y: y(s.valores[ult] as number) + 4 }
        })
        .sort((a, b) => a.y - b.y)
  for (let i = 1; i < rotulos.length; i++) if (rotulos[i].y - rotulos[i - 1].y < 13) rotulos[i].y = rotulos[i - 1].y + 13

  const mover = (ev: React.PointerEvent): void => {
    const svg = svgRef.current
    if (!svg) return
    const bb = svg.getBoundingClientRect()
    const px = ((ev.clientX - bb.left) * W) / bb.width
    const X = pontos ? series[0].x ?? [] : xs
    let melhor = 0
    X.forEach((v, i) => {
      if (Math.abs(x(v) - px) < Math.abs(x(X[melhor]) - px)) melhor = i
    })
    if (!pontos) setCruz(melhor)
    setDica({
      ...posicao(caixaRef.current, ev),
      titulo: tituloDica(melhor),
      linhas: series.map((s, i) => ({
        nome: s.nome,
        cor: cor(i),
        valor: s.valores[melhor] === null ? '—' : fmtY(s.valores[melhor] as number),
      })),
    })
  }

  return (
    <Moldura
      titulo={titulo}
      sub={sub}
      entenda={entenda}
      dica={dica}
      caixaRef={caixaRef}
      legenda={series.map((s, i) => ({ nome: s.nome, cor: cor(i), caixa: pontos }))}
      dados={
        pontos
          ? {
              cabecalho: ['', ...series.map((s) => `${s.nome} (previsto → real)`)],
              linhas: (series[0].x ?? []).map((_, i) => [
                tituloDica(i),
                ...series.map((s) => `${fmtX((s.x ?? [])[i])} → ${fmtY(s.valores[i] as number)}`),
              ]),
            }
          : {
              cabecalho: ['', ...series.map((s) => s.nome)],
              linhas: xs.map((v, i) => [fmtX(v), ...series.map((s) => (s.valores[i] === null ? '—' : fmtY(s.valores[i] as number)))]),
            }
      }
    >
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={titulo}>
        {yticks.map((t) => (
          <g key={t}>
            <line x1={M.l} x2={W - M.r} y1={y(t)} y2={y(t)} stroke={GRADE} strokeDasharray="2 3" />
            <text x={M.l - 6} y={y(t) + 4} textAnchor="end" fontSize={11} fill={EIXO}>
              {fmtY(t)}
            </text>
          </g>
        ))}
        {xticks.map((t) => (
          <text key={t} x={x(t)} y={H - 10} textAnchor="middle" fontSize={11} fill={EIXO}>
            {fmtX(t)}
          </text>
        ))}
        {pontos && <line x1={x(0)} y1={y(0)} x2={x(1)} y2={y(1)} stroke="#B8C0CC" strokeWidth={1.5} strokeDasharray="4 4" />}
        {series.map((s, i) => {
          const pts = s.valores
            .map((v, j) => (v === null ? null : [x(s.x ? s.x[j] : xs[j]), y(v)]))
            .filter((p): p is number[] => p !== null)
          return pontos ? (
            pts.map((p) => <circle key={`${i}-${p[0]}`} cx={p[0]} cy={p[1]} r={5} fill={cor(i)} stroke="#fff" strokeWidth={2} />)
          ) : (
            <path
              key={s.nome}
              d={'M' + pts.map((p) => p.join(',')).join('L')}
              fill="none"
              stroke={cor(i)}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeDasharray={s.tracejada ? '5 4' : undefined}
            />
          )
        })}
        {rotulos.map((r) => (
          <g key={r.nome}>
            <line x1={r.x - 6} x2={r.x - 2} y1={r.y - 4} y2={r.y - 4} stroke={r.cor} strokeWidth={2} />
            <text x={r.x + 1} y={r.y} fontSize={11} fontWeight={600} fill={TINTA}>
              {r.nome}
            </text>
          </g>
        ))}
        {cruz !== null && <line x1={x(xs[cruz])} x2={x(xs[cruz])} y1={M.t} y2={H - M.b} stroke={EIXO} />}
        <rect
          x={M.l}
          y={M.t}
          width={W - M.l - M.r}
          height={H - M.t - M.b}
          fill="transparent"
          onPointerMove={mover}
          onPointerLeave={() => {
            setDica(null)
            setCruz(null)
          }}
        />
      </svg>
    </Moldura>
  )
}

/* ── barras horizontais ── */
export const GraficoBarras: FC<{
  titulo: string
  sub?: string
  entenda?: string[]
  categorias: string[]
  valores: number[]
  max: number
  ticks: number[]
  fmt?: (v: number) => string
  /** linha de referência (ex.: 1 = sem mudança) */
  referencia?: number
}> = ({ titulo, sub, entenda, categorias, valores, max, ticks, fmt = pctFmt, referencia }) => {
  const caixaRef = useRef<HTMLDivElement>(null)
  const [dica, setDica] = useState<EstadoDica | null>(null)
  const W = 560
  const L = 160
  const linha = 26
  const M = { t: 8, b: 26, r: 50 }
  const H = M.t + M.b + categorias.length * linha
  const x = (v: number): number => L + ((W - L - M.r) * v) / max
  return (
    <Moldura
      titulo={titulo}
      sub={sub}
      entenda={entenda}
      dica={dica}
      caixaRef={caixaRef}
      dados={{ cabecalho: ['', 'Valor'], linhas: categorias.map((c, i) => [c, fmt(valores[i])]) }}
    >
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={titulo}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={x(t)} x2={x(t)} y1={M.t} y2={H - M.b} stroke={GRADE} strokeDasharray="2 3" />
            <text x={x(t)} y={H - 8} textAnchor="middle" fontSize={11} fill={EIXO}>
              {fmt(t)}
            </text>
          </g>
        ))}
        {referencia !== undefined && <line x1={x(referencia)} x2={x(referencia)} y1={M.t} y2={H - M.b} stroke={EIXO} strokeWidth={1.5} />}
        {categorias.map((c, i) => {
          const yy = M.t + i * linha + 6
          const w = Math.max(2, x(valores[i]) - L)
          return (
            <g
              key={c}
              onPointerMove={(ev) =>
                setDica({ ...posicao(caixaRef.current, ev), titulo: c, linhas: [{ nome: titulo, cor: SERIE[0], valor: fmt(valores[i]) }] })
              }
              onPointerLeave={() => setDica(null)}
            >
              <text x={L - 8} y={yy + 11} textAnchor="end" fontSize={12} fill={TINTA}>
                {c}
              </text>
              <rect x={L} y={yy} width={w} height={14} rx={3} fill={SERIE[0]} />
              <text x={L + w + 5} y={yy + 11} fontSize={11} fontWeight={600} fill={TINTA}>
                {fmt(valores[i])}
              </text>
            </g>
          )
        })}
      </svg>
    </Moldura>
  )
}

/* ── barras empilhadas (100%) ── */
export const GraficoEmpilhado: FC<{
  titulo: string
  sub?: string
  entenda?: string[]
  categorias: string[]
  series: Array<{ nome: string; valores: number[] }>
  /** categorias com fundo destacado (ex.: pandemia) */
  destaque?: string[]
  rotuloDestaque?: string
}> = ({ titulo, sub, entenda, categorias, series, destaque = [], rotuloDestaque }) => {
  const caixaRef = useRef<HTMLDivElement>(null)
  const [dica, setDica] = useState<EstadoDica | null>(null)
  const W = 560
  const H = 250
  const M = { l: 40, r: 12, t: 10, b: 30 }
  const bw = (W - M.l - M.r) / categorias.length
  const y = (v: number): number => H - M.b - (H - M.t - M.b) * v
  return (
    <Moldura
      titulo={titulo}
      sub={sub}
      entenda={entenda}
      dica={dica}
      caixaRef={caixaRef}
      legenda={series.map((s, i) => ({ nome: s.nome, cor: SERIE[i], caixa: true }))}
      dados={{
        cabecalho: ['', ...series.map((s) => s.nome)],
        linhas: categorias.map((c, i) => [c, ...series.map((s) => pctFmt(s.valores[i]))]),
      }}
    >
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={titulo}>
        {[0, 0.25, 0.5, 0.75, 1].map((t) => (
          <g key={t}>
            <line x1={M.l} x2={W - M.r} y1={y(t)} y2={y(t)} stroke={GRADE} strokeDasharray="2 3" />
            <text x={M.l - 6} y={y(t) + 4} textAnchor="end" fontSize={11} fill={EIXO}>
              {pctFmt(t)}
            </text>
          </g>
        ))}
        {categorias.map((c, i) => {
          const x0 = M.l + i * bw + 5
          const w = bw - 10
          let acc = 0
          return (
            <g
              key={c}
              onPointerMove={(ev) =>
                setDica({
                  ...posicao(caixaRef.current, ev),
                  titulo: c + (destaque.indexOf(c) >= 0 && rotuloDestaque ? ` · ${rotuloDestaque}` : ''),
                  linhas: series.map((s, j) => ({ nome: s.nome, cor: SERIE[j], valor: pctFmt(s.valores[i]) })),
                })
              }
              onPointerLeave={() => setDica(null)}
            >
              {destaque.indexOf(c) >= 0 && <rect x={M.l + i * bw + 1} y={M.t} width={bw - 2} height={H - M.t - M.b} fill="#EEF2F8" />}
              {series.map((s, j) => {
                const v = s.valores[i]
                const topo = y(acc + v)
                const h = (H - M.t - M.b) * v
                acc += v
                return (
                  <g key={s.nome}>
                    <rect x={x0} y={topo} width={w} height={Math.max(0, h - 2)} rx={2} fill={SERIE[j]} />
                    {j === 0 && (
                      <text x={x0 + w / 2} y={topo + 14} textAnchor="middle" fontSize={11} fontWeight={600} fill="#fff">
                        {Math.round(v * 100)}%
                      </text>
                    )}
                  </g>
                )
              })}
              <text x={x0 + w / 2} y={H - 10} textAnchor="middle" fontSize={11} fill={EIXO}>
                {c}
              </text>
            </g>
          )
        })}
      </svg>
    </Moldura>
  )
}

/* ── faixa em escala log (mesmo artista em estados diferentes) ── */
export const GraficoFaixaLog: FC<{
  titulo: string
  sub?: string
  entenda?: string[]
  /** [nome, nº de eventos, mediana geral, menor UF, maior UF, nº de UFs] */
  linhas: Array<[string, number, number, number, number, number]>
}> = ({ titulo, sub, entenda, linhas }) => {
  const caixaRef = useRef<HTMLDivElement>(null)
  const [dica, setDica] = useState<EstadoDica | null>(null)
  const W = 560
  const L = 130
  const passo = 24
  const M = { t: 8, b: 28, r: 20 }
  const H = M.t + M.b + linhas.length * passo
  const lx = (v: number): number => L + ((W - L - M.r) * (Math.log10(v) - 2)) / (Math.log10(15000) - 2)
  const n = (v: number): string => Math.round(v).toLocaleString('pt-BR')
  const legenda = [
    { nome: 'UF de menor venda', cor: SERIE[1], caixa: true },
    { nome: 'Mediana geral', cor: TINTA, caixa: true },
    { nome: 'UF de maior venda', cor: SERIE[0], caixa: true },
  ]
  return (
    <Moldura
      titulo={titulo}
      sub={sub}
      entenda={entenda}
      dica={dica}
      caixaRef={caixaRef}
      legenda={legenda}
      dados={{
        cabecalho: ['Artista', 'Eventos', 'UFs', 'Menor UF', 'Mediana geral', 'Maior UF'],
        linhas: linhas.map(([nome, eventos, med, min, max, ufs]) => [nome, n(eventos), n(ufs), n(min), n(med), n(max)]),
      }}
    >
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={titulo}>
        {[100, 300, 1000, 3000, 10000].map((t) => (
          <g key={t}>
            <line x1={lx(t)} x2={lx(t)} y1={M.t} y2={H - M.b} stroke={GRADE} strokeDasharray="2 3" />
            <text x={lx(t)} y={H - 10} textAnchor="middle" fontSize={11} fill={EIXO}>
              {n(t)}
            </text>
          </g>
        ))}
        {linhas.map(([nome, eventos, med, min, max, ufs], i) => {
          const yy = M.t + i * passo + 12
          return (
            <g
              key={nome}
              onPointerMove={(ev) =>
                setDica({
                  ...posicao(caixaRef.current, ev),
                  titulo: `${nome} · ${eventos} eventos em ${ufs} UFs`,
                  linhas: [
                    { nome: 'Menor UF', cor: SERIE[1], valor: n(min) },
                    { nome: 'Mediana geral', cor: TINTA, valor: n(med) },
                    { nome: 'Maior UF', cor: SERIE[0], valor: n(max) },
                  ],
                })
              }
              onPointerLeave={() => setDica(null)}
            >
              <text x={L - 8} y={yy + 4} textAnchor="end" fontSize={12} fill={TINTA}>
                {nome}
              </text>
              <line x1={lx(min)} x2={lx(max)} y1={yy} y2={yy} stroke="#B8C0CC" strokeWidth={3} strokeLinecap="round" />
              <circle cx={lx(min)} cy={yy} r={5} fill={SERIE[1]} stroke="#fff" strokeWidth={2} />
              <circle cx={lx(max)} cy={yy} r={5} fill={SERIE[0]} stroke="#fff" strokeWidth={2} />
              <line x1={lx(med)} x2={lx(med)} y1={yy - 6} y2={yy + 6} stroke={TINTA} strokeWidth={2} />
            </g>
          )
        })}
      </svg>
    </Moldura>
  )
}
